import { useEffect, useRef } from "react";
import { useUser } from "@clerk/clerk-react";
import { posthog, whenPostHogReady } from "@/lib/analytics/client";

// Marketing-email gateway: cloud.specstory.com/pricing plants the shared
// cross-subdomain PostHog identity cookie (when this browser has a cloud
// session), then forwards to the marketing pricing page — whose PostHog init
// adopts the identified distinct_id from that cookie, attributing the visit
// to the signed-in user. Signed-out visitors are simply redirected. Query
// params (utm_*) are passed through, and the pageview captured here doubles
// as a click signal for campaigns that link to this URL.
const DEST = "https://specstory.com/pricing";

export default function PricingRedirect() {
  const { isLoaded, isSignedIn, user } = useUser();
  const redirected = useRef(false);

  useEffect(() => {
    if (!isLoaded || redirected.current) return;
    let cancelled = false;
    const go = () => {
      if (cancelled || redirected.current) return;
      redirected.current = true;
      window.location.replace(DEST + window.location.search);
    };
    (async () => {
      // Wait for the lazily-loaded posthog client — capped so ad-blocked or
      // analytics-disabled visitors aren't stranded on this page.
      await Promise.race([
        whenPostHogReady(),
        new Promise((resolve) => setTimeout(resolve, 2500)),
      ]);
      // Re-assert identity so the shared cookie is written even if the
      // app-level identify hasn't run yet (no-op when it has). The cookie
      // write inside identify's persistence save is synchronous.
      if (isSignedIn && user) posthog.identify(user.id);
      // A beat for the $pageview/$identify flush to get on the wire.
      setTimeout(go, 200);
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoaded, isSignedIn, user]);

  return (
    <div className="flex min-h-screen items-center justify-center">
      <p className="text-sm text-muted-foreground">Taking you to pricing…</p>
    </div>
  );
}
